import type { VoidFunctionComponent } from 'react';
import { icons } from '../../../../generic/Icon';
import useTeamPermissions from '../hooks/useTeamPermissions';
import SettingsTableRowActions from '../../../shared/SettingsTableRowActions';

type Props = {
  teamId: number;
  toEdit: string;
  onDelete: () => void;
  isDeleting: boolean;
  isTeamGroups?: boolean;
};

/**
 * Generic table row actions component that can be used for teams, team groups, etc.
 * Renders a popper menu with the provided items.
 *
 * @param {Props} props - The props for the component.
 * @param {Array<{label: string, onClick: () => void, isDisabled?: boolean, isWarning?: boolean}>} props.items - The menu items to display.
 * @returns {JSX.Element} The component.
 */
const TableRowActions: VoidFunctionComponent<Props> = ({
  teamId,
  toEdit,
  onDelete,
  isDeleting,
  isTeamGroups = false,
}) => {
  const {
    canEditAnyTeam,
    canDeleteAnyTeam,
    canEditThisTeam,
    canViewThisTeam,
  } = useTeamPermissions(teamId);

  const canEdit = canEditAnyTeam || canEditThisTeam;

  return (
    <SettingsTableRowActions>
      <SettingsTableRowActions.ViewOrEditLink
        to={toEdit}
        tooltip={canEdit ? 'Edit' : 'View'}
        tooltipId={`view-edit-team-${teamId}`}
        isDisabled={isDeleting || !canViewThisTeam}
        canEdit={canEdit}
      />
      <SettingsTableRowActions.IconButton
        icon={icons.trashRegular}
        onClick={onDelete}
        tooltipId={`delete-team-group-${teamId}`}
        tooltip={!canDeleteAnyTeam ? `You do not have permission to delete this team${isTeamGroups ? ' group' : ''}.` : 'Delete'}
        isDisabled={isDeleting || !canDeleteAnyTeam}
      />
    </SettingsTableRowActions>
  );
};

export default TableRowActions;
